import {
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Link,
  Modal, ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  useDisclosure
} from "@chakra-ui/react";
import {AxiosResponse} from "axios";
import {Field, Form, Formik} from "formik";
import React, {useState} from "react";
import {useMutation, useQuery, useQueryClient} from "react-query";
import {useNavigate} from "react-router-dom";

import {getRegisterPeriod, submitLicenseRequestForm} from "../shared/Api";
import {disableBlurProtection} from "../shared/hooks/BlurProtection";
import {customButtonProps, EducationType, Paths} from "../shared/model/Constants";
import {LicenseRequestDto, ResponseDto} from "../shared/model/Dto";
import {authenticationKeys, publicKeys} from "../shared/QueryKeys";
import {enableKeyCommands, extractData, isInAvataiSite} from "../shared/Utils";

const RegisterAvatai = () => {
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const queryClient = useQueryClient();

  const [id, setId] = useState('');
  const [IDConfirm, setIDConfirm] = useState('');
  const [name, setName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [emailConfirm, setEmailConfirm] = useState('');
  const [email2, setEmail2] = useState('');
  const [phone, setPhone] = useState('');
  const [phone2, setPhone2] = useState('');
  const [age, setAge] = useState(0);
  const [country, setCountry] = useState('');
  const [language, setLanguage] = useState('');
  const [region, setRegion] = useState('');
  const [institution, setInstitution] = useState('');
  const [sex, setSex] = useState('Masculino');
  const [education, setEducation] = useState(EducationType.NONE);
  const [acceptsTC, setAcceptsTC] = useState<boolean>(false);
  const [error, setError] = useState('');

  const invalidID = id !== IDConfirm;
  const invalidAge = age <= 0;
  const invalidEmail =  email !== emailConfirm;
  const missingValue =  !id.trim() || !IDConfirm.trim() ||
    !name.trim() || !lastName.trim() || invalidID || invalidAge ||  invalidEmail ||
    !email.trim() || !emailConfirm.trim() || !acceptsTC;

  disableBlurProtection();
  enableKeyCommands();

  if ( !isInAvataiSite())
    navigate(Paths.HOME, { replace: true });

  const registerPeriodQuery = useQuery(publicKeys.licenseRequest(), () =>
  { return getRegisterPeriod().then(extractData); });

  if (registerPeriodQuery.isFetched && registerPeriodQuery.data===false) {
    navigate(Paths.AVATAI_INTRO);
  }

  const submitForm = useMutation<AxiosResponse<ResponseDto>, Error, LicenseRequestDto>(
    (params: LicenseRequestDto) => {
      setError("");
      return submitLicenseRequestForm(
        {
          id: id.trim(),
          name: name.trim()+" "+lastName.trim(),
          institution: institution.trim(),
          region: region.trim(),
          sex: sex,
          age: age,
          phone: phone.trim(),
          phone2: phone2.trim(),
          email: email.trim(),
          email2: email2.trim(),
          language: language.trim(),
          education: education,
          country: language.trim()
        }
      );
    },
    {
      onSuccess: (TData) => {
        if (TData.data.success)
          onOpen();
        else
          setError(TData.data.message);

        return queryClient.invalidateQueries(authenticationKeys.currentUser());
      },
    }
  );

  disableBlurProtection();
  enableKeyCommands();

  return (
    <Box maxW="container.lg" minW="100%" textAlign="center"
         paddingTop="1em" paddingBottom="2em">

      <Modal blockScrollOnMount={false} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Registro exitoso</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>Sus datos de ingreso han sido enviados por correo</Text>
          </ModalBody>
          <ModalFooter>
            <Button
              {...customButtonProps(true)}
              type="submit"
              onClick={()=>navigate(Paths.LOGIN)}
            >
              Iniciar sesión
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Heading width="100%">Regístrate</Heading>
      <Flex justify="center" flexDirection="column" alignItems="center" paddingTop="2em">
        <Container maxW="container.lg">
          <Formik
            initialValues={
              {
                id: "",
                name: "",
                institution: "",
                region: "",
                sex: "",
                age: 0,
                phone: "",
                phone2: "",
                email: "",
                email2: "",
                language: "",
                education: EducationType.NONE,
                country: ""
              }
            }
            onSubmit={(values: LicenseRequestDto) =>
              submitForm.mutate(values)
            }
          >
            <Form>
              <HStack>
                <Field name="ID">
                  {({ field }: any) => (
                    <FormControl isRequired isInvalid={invalidID}>
                      <FormLabel htmlFor="ID">Identificación</FormLabel>
                      <Input {...field} id="ID"
                             onChange={(e)=>
                               setId(e.target.value)}
                             value={id}
                             onPaste={(e)=>{
                               e.preventDefault()
                               return false;
                             }}
                             onCopy={(e)=>{
                               e.preventDefault()
                               return false;
                             }}  />
                    </FormControl>
                  )}
                </Field>
                <Field name="IDConfirm">
                  {({ field }: any) => (
                    <FormControl isRequired isInvalid={invalidID}>
                      <FormLabel htmlFor="IDConfirm">Confirmar identificación</FormLabel>
                      <Input {...field} id="IDConfirm"
                             onChange={(e)=>
                               setIDConfirm(e.target.value)}
                             value={IDConfirm}
                             onPaste={(e)=>{
                               e.preventDefault()
                               return false;
                             }}
                             onCopy={(e)=>{
                               e.preventDefault()
                               return false;
                             }}  />
                    </FormControl>
                  )}
                </Field>
              </HStack>
              <HStack>
                <Field name="name">
                  {({ field }: any) => (
                    <FormControl isRequired>
                      <FormLabel htmlFor="name">Nombre</FormLabel>
                      <Input {...field} id="name"
                             onChange={(e)=>
                               setName(e.target.value?.toUpperCase())}
                             value={name}/>
                    </FormControl>
                  )}
                </Field>
                <Field name="lastName">
                  {({ field }: any) => (
                    <FormControl isRequired>
                      <FormLabel htmlFor="lastName">Apellidos</FormLabel>
                      <Input {...field} id="lastName"
                             onChange={(e)=>
                               setLastName(e.target.value?.toUpperCase())}
                             value={lastName}/>
                    </FormControl>
                  )}
                </Field>
              </HStack>
              <HStack>
                <Field name="email">
                  {({ field }: any) => (
                    <FormControl isRequired isInvalid={invalidEmail}>
                      <FormLabel htmlFor="email">Correo electrónico</FormLabel>
                      <Input {...field} id="email" type='email'
                             onChange={(e)=>
                               setEmail(e.target.value.toUpperCase())}
                             value={email}/>
                    </FormControl>
                  )}
                </Field>
                <Field name="emailC">
                  {({ field }: any) => (
                    <FormControl isRequired isInvalid={invalidEmail}>
                      <FormLabel htmlFor="emailC">Confirmar correo electrónico</FormLabel>
                      <Input {...field} id="emailC" type='email'
                             onChange={(e)=>
                               setEmailConfirm(e.target.value?.toUpperCase())}
                             value={emailConfirm}/>
                    </FormControl>
                  )}
                </Field>
              </HStack>
              <HStack>
                <Field name="phone">
                  {({ field }: any) => (
                    <FormControl isRequired>
                      <FormLabel htmlFor="phone">Teléfono</FormLabel>
                      <Input {...field} id="phone" type='number'
                             onChange={(e)=>
                               setPhone(e.target.value)}
                             value={phone}/>
                    </FormControl>
                  )}
                </Field>

                <Field name="sex">
                  {({ field }: any) => (
                    <FormControl isRequired>
                      <FormLabel htmlFor="sex">Sexo</FormLabel>
                      <Select {...field} id="sex"
                              onChange={(e)=>
                                setSex(e.target.value )} value={sex}>
                        <option value={'Masculino'}>Masculino</option>
                        <option value={'Femenino'}>Femenino</option>
                        <option value={'Otro'}>Otro</option>
                      </Select>
                    </FormControl>
                  )}
                </Field>
                <Field name="age">
                  {({ field }: any) => (
                    <FormControl isRequired isInvalid={invalidAge}>
                      <FormLabel htmlFor="age">Edad</FormLabel>
                      <Input {...field} id="age" type="number"
                             onChange={(e)=>
                               setAge(Number(e.target.value))}
                             value={age}/>
                    </FormControl>
                  )}
                </Field>
              </HStack>
              <HStack>
                <Field name="country">
                  {({ field }: any) => (
                    <FormControl isRequired>
                      <FormLabel htmlFor="country">País de origen</FormLabel>
                      <Input {...field} id="country"
                             onChange={(e)=>
                               setCountry(e.target.value.toUpperCase())}
                             value={country}/>
                    </FormControl>
                  )}
                </Field>
                <Field name="language">
                  {({ field }: any) => (
                    <FormControl isRequired>
                      <FormLabel htmlFor="language">Lengua nativa</FormLabel>
                      <Input {...field} id="language"
                             onChange={(e)=>
                               setLanguage(e.target.value.toUpperCase())}
                             value={language}/>
                    </FormControl>
                  )}
                </Field>
                <Field name="education">
                  {({ field }: any) => (
                    <FormControl isRequired>
                      <FormLabel htmlFor="education">Educación</FormLabel>
                      <Select {...field} id="education"
                              onChange={(e)=>
                                setEducation(EducationType[e.target.value as keyof typeof EducationType])}
                              value={education}>
                        <option value={EducationType.PRIMARY_INCOMPLETE}>Primaria incompleta</option>
                        <option value={EducationType.PRIMARY_COMPLETE}>Primaria completa</option>
                        <option value={EducationType.SECONDARY_INCOMPLETE}>Secundaria incompleta</option>
                        <option value={EducationType.SECONDARY_COMPLETE}>Secundaria completa</option>
                        <option value={EducationType.TECHNICAL}>Técnico</option>
                        <option value={EducationType.DIPLOMAT}>Diplomado</option>
                        <option value={EducationType.UNIVERSITY_INCOMPLETE}>Universitaria incompleta</option>
                        <option value={EducationType.UNIVERSITY_COMPLETE}>Universitaria completa</option>
                        <option value={EducationType.NONE}>Ninguna</option>
                      </Select>
                    </FormControl>
                  )}
                </Field>
              </HStack>
              <HStack>
                <Field name="email2">
                  {({ field }: any) => (
                    <FormControl>
                      <FormLabel htmlFor="email2">Correo electrónico secundario</FormLabel>
                      <Input {...field} id="email2" type='email'
                             onChange={(e)=>
                               setEmail2(e.target.value.toUpperCase())}
                             value={email2}/>
                    </FormControl>
                  )}
                </Field>
                <Field name="phone2">
                  {({ field }: any) => (
                    <FormControl>
                      <FormLabel htmlFor="phone2">Teléfono secundario</FormLabel>
                      <Input {...field} id="phone2" type='number'
                             onChange={(e)=>
                               setPhone2(e.target.value)}
                             value={phone2}/>
                    </FormControl>
                  )}
                </Field>
              </HStack>
              <HStack>
                <Field name="institution">
                  {({ field }: any) => (
                    <FormControl>
                      <FormLabel htmlFor="institution">Institución</FormLabel>
                      <Input {...field} id="institution"
                             onChange={(e)=>
                               setInstitution(e.target.value.toUpperCase())}
                             value={institution}/>
                    </FormControl>
                  )}
                </Field>
                <Field name="region">
                  {({ field }: any) => (
                    <FormControl >
                      <FormLabel htmlFor="region">Provincia</FormLabel>
                      <Input {...field} id="region"
                             onChange={(e)=>
                               setRegion(e.target.value.toUpperCase())}
                             value={region}/>
                    </FormControl>
                  )}
                </Field>
              </HStack>
              <Checkbox minW="fit-content" marginY="1em"
                        onChange={ (e) => {
                          setAcceptsTC(e.target.checked);
                        }}
                        defaultChecked={acceptsTC}>
                <Link href='/docs/Términos y Condiciones AvatAI.pdf' isExternal>
                  Términos y Condiciones
                </Link>
              </Checkbox>
              <HStack justifyContent="center" >
                <Button
                  {...customButtonProps(true)}
                  onClick={()=>navigate(Paths.AVATAI_INTRO)}
                >
                  Volver
                </Button>
                <Button
                  {...customButtonProps(true)}
                  type="submit"
                  disabled={missingValue}
                  _disabled={{
                    cursor: 'default',
                    opacity: '0.4',
                  }}
                >
                  Registrarme
                </Button>
              </HStack>
            </Form>
          </Formik>
          <Text marginY="1em">{error}</Text>
        </Container>

      </Flex>
    </Box>
  );
};

export default RegisterAvatai;
